import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Lock,
  Unlock,
  Key,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const EVENT_TYPES = [
  { value: '', label: 'Wszystkie zdarzenia' },
  { value: 'ACCOUNT_LOCKED', label: 'Blokada konta' },
  { value: 'LOGIN_FAILED', label: 'Nieudane logowanie' },
  { value: 'LOGIN_TOTP_FAILED', label: 'Błędny kod TOTP' },
  { value: 'LOGIN_SUCCESS', label: 'Logowanie pomyślne' },
  { value: 'MFA_SETUP_COMPLETED', label: 'Konfiguracja MFA' },
  { value: 'MFA_RESET_BY_ADMIN', label: 'Reset TOTP przez admina' },
  { value: 'ACCOUNT_UNLOCKED', label: 'Odblokowanie konta' },
];

const EVENT_META = {
  ACCOUNT_LOCKED: { icon: Lock, color: 'text-red-700 bg-red-50 border-red-200', label: 'Blokada konta' },
  LOGIN_FAILED: { icon: AlertTriangle, color: 'text-amber-700 bg-amber-50 border-amber-200', label: 'Nieudane logowanie' },
  LOGIN_TOTP_FAILED: { icon: Key, color: 'text-orange-700 bg-orange-50 border-orange-200', label: 'Błędny kod TOTP' },
  LOGIN_SUCCESS: { icon: CheckCircle, color: 'text-green-700 bg-green-50 border-green-200', label: 'Logowanie pomyślne' },
  MFA_SETUP_COMPLETED: { icon: Shield, color: 'text-blue-700 bg-blue-50 border-blue-200', label: 'Konfiguracja MFA' },
  MFA_RESET_BY_ADMIN: { icon: RefreshCw, color: 'text-indigo-700 bg-indigo-50 border-indigo-200', label: 'Reset TOTP' },
  ACCOUNT_UNLOCKED: { icon: Unlock, color: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Odblokowanie konta' },
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return value;
  }
};

const SecurityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, size: 25 };
      if (eventType) params.eventType = eventType;
      const response = await api.get('/admin/security-logs', { params });
      setLogs(response.data.content || []);
      setTotalPages(response.data.totalPages || 0);
      setTotalElements(response.data.totalElements || 0);
    } catch (error) {
      console.error('Error fetching security logs:', error);
      toast.error(error.response?.data?.error || 'Nie udało się pobrać dziennika zdarzeń');
    } finally {
      setLoading(false);
    }
  }, [page, eventType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (value) => {
    setEventType(value);
    setPage(0);
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary-600" />
              Dziennik zdarzeń bezpieczeństwa
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Monitoring logowań, blokad kont i konfiguracji MFA w systemie CRM
            </p>
          </div>
          <button
            type="button"
            onClick={fetchLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Odśwież
          </button>
        </div>

        <div className="card-body border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Filter className="h-4 w-4" />
              Filtr zdarzeń
            </div>
            <select
              value={eventType}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {EVENT_TYPES.map((type) => (
                <option key={type.value || 'all'} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-500 sm:ml-auto">
              Łącznie: <strong>{totalElements}</strong> zdarzeń
            </span>
          </div>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              Brak zdarzeń do wyświetlenia
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Data i czas</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Zdarzenie</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Użytkownik</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Adres IP</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Szczegóły</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {logs.map((log) => {
                    const meta = EVENT_META[log.eventType] || {
                      icon: Shield,
                      color: 'text-gray-700 bg-gray-50 border-gray-200',
                      label: log.eventType,
                    };
                    const Icon = meta.icon;
                    return (
                      <tr key={log.id} className="hover:bg-gray-50/80">
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {formatDateTime(log.createdAt)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${meta.color}`}>
                            <Icon className="h-3.5 w-3.5" />
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {log.userEmail || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-700">
                          {log.ipAddress || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-md">
                          {log.details || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="card-body border-t border-gray-100 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </button>
            <span className="text-sm text-gray-600">
              Strona {page + 1} z {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || loading}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg disabled:opacity-50"
            >
              Następna
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-900">
          <strong>Polityka bezpieczeństwa:</strong> po 3 nieudanych próbach logowania konto jest blokowane na 10 minut.
          Administratorzy otrzymują powiadomienie e-mail o każdej automatycznej blokadzie wraz z adresem IP.
        </p>
      </div>
    </div>
  );
};

export default SecurityLogs;
